import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faInfoCircle,
  faDownload,
  faExpand,
  faFilter,
  faShip,
  faTruck,
  faPlane,
  faLightbulb,
} from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  TimeScale,
  ScatterController,
} from "chart.js";
import { Scatter } from "react-chartjs-2";
import {
  format,
  subDays,
  startOfWeek,
  endOfWeek,
  isWithinInterval,
} from "date-fns";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { calculateBusinessHours, KELLANOVA_THRESHOLDS, getPerformanceCategory } from "../utils/businessHours";

// Register Chart.js components
ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  TimeScale,
  ScatterController,
  ChartDataLabels
);

const ProcessingEfficiencyMatrixChart = ({
  data,
  isLoading,
  onChartItemClick,
  height = "100%",
}) => {
  // State for chart data
  const [chartData, setChartData] = useState({
    datasets: [],
  });

  // State for insights derived from data
  const [insights, setInsights] = useState({
    totalReferences: 0,
    optimalEntryPoints: [],
    bottleneckEntryPoints: [],
    optimalVolumeThreshold: 0,
    recentTrend: "",
    recommendedActions: [],
  });

  // State for time period filter
  const [timePeriod, setTimePeriod] = useState("all"); // all, month, week

  // State for expanded chart view
  const [isExpanded, setIsExpanded] = useState(false);

  // Date helper functions
  const getDateRange = (period) => {
    const now = new Date();
    let startDate;

    switch (period) {
      case "week":
        startDate = subDays(now, 7);
        break;
      case "month":
        startDate = subDays(now, 30);
        break;
      case "quarter":
        startDate = subDays(now, 90);
        break;
      default: // "all"
        startDate = new Date(0); // Earliest possible date
    }

    return { startDate, endDate: now };
  };

  // Transport icon helper
  const getTransportIcon = (entryPoint) => {
    if (!entryPoint) return { icon: faInfoCircle, color: "text-gray-500" };

    const entryPointNum = parseInt(entryPoint, 10);

    // Sea freight entry points
    if ([160, 510, 430].includes(entryPointNum)) {
      return { icon: faShip, color: "text-blue-600" };
    }

    // Road freight entry points
    if ([240].includes(entryPointNum)) {
      return { icon: faTruck, color: "text-green-600" };
    }

    // Air freight entry points
    if ([520, 521, 640].includes(entryPointNum)) {
      return { icon: faPlane, color: "text-purple-600" };
    }

    return { icon: faInfoCircle, color: "text-gray-500" };
  };

  // Process data when it changes
  useEffect(() => {
    if (!isLoading && data && data.length > 0) {
      processChartData();
    }
  }, [data, isLoading, timePeriod]);

  // Process chart data
  const processChartData = () => {
    // Get date range based on selected period
    const { startDate, endDate } = getDateRange(timePeriod);

    // Filter data by date range
    const filteredData = data.filter((item) => {
      const itemDate = new Date(item.header?.detail?.dates_data?.date_init);
      return (
        !isNaN(itemDate.getTime()) &&
        itemDate >= startDate &&
        itemDate <= endDate
      );
    });

    if (filteredData.length === 0) {
      setChartData({ datasets: [] });
      return;
    }

    // Group data by entry point
    const entryPointData = {};
    const weeklyVolumes = {};
    const entryPointColors = {
      // Blue hues for sea freight
      160: "rgba(54, 162, 235, 0.7)",
      430: "rgba(25, 118, 210, 0.7)",
      510: "rgba(66, 139, 202, 0.7)",

      // Green hues for road freight
      240: "rgba(75, 192, 192, 0.7)",

      // Purple hues for air freight
      520: "rgba(153, 102, 255, 0.7)",
      521: "rgba(126, 87, 194, 0.7)",
      640: "rgba(103, 58, 183, 0.7)",

      // Default
      unknown: "rgba(201, 203, 207, 0.7)",
    };

    filteredData.forEach((item) => {
      const entryPoint =
        item.header?.pedimento?.pedimento_entry_point || "unknown";
      const dateInit = new Date(item.header?.detail?.dates_data?.date_init);
      const dateCrossing = new Date(
        item.header?.detail?.dates_data?.date_crossing
      );

      // Calculate processing time using business hours (excluding weekends)
      let processingTime = 0;
      if (!isNaN(dateInit.getTime()) && !isNaN(dateCrossing.getTime())) {
        processingTime = calculateBusinessHours(dateInit, dateCrossing);
      }

      // Skip invalid processing times
      if (processingTime <= 0) return;

      // Initialize entry point data if not exists
      if (!entryPointData[entryPoint]) {
        entryPointData[entryPoint] = {
          entryPoint,
          points: [],
          totalTime: 0,
          count: 0,
          avgTime: 0,
          weeklyVolumes: {},
          dataPoints: [],
        };
      }

      // Add to entry point data
      entryPointData[entryPoint].totalTime += processingTime;
      entryPointData[entryPoint].count += 1;

      // Track weekly volumes
      const weekStart = format(startOfWeek(dateInit), "yyyy-MM-dd");
      entryPointData[entryPoint].weeklyVolumes[weekStart] =
        (entryPointData[entryPoint].weeklyVolumes[weekStart] || 0) + 1;

      // Add data point
      entryPointData[entryPoint].dataPoints.push({
        date: dateInit,
        processingTime,
      });
    });

    // Calculate averages and prepare chart data
    const datasets = [];
    const insights = {
      totalReferences: 0,
      optimalEntryPoints: [],
      bottleneckEntryPoints: [],
      optimalVolumeThreshold: 0,
      recentTrend: "",
      recommendedActions: [],
    };

    // For each entry point, calculate weekly average processing time and volume
    Object.keys(entryPointData).forEach((entryPoint) => {
      const epData = entryPointData[entryPoint];
      const weeklyData = {};

      // Group data points by week
      epData.dataPoints.forEach((point) => {
        const weekStart = format(startOfWeek(point.date), "yyyy-MM-dd");
        if (!weeklyData[weekStart]) {
          weeklyData[weekStart] = {
            times: [],
            totalTime: 0,
            count: 0,
          };
        }

        weeklyData[weekStart].times.push(point.processingTime);
        weeklyData[weekStart].totalTime += point.processingTime;
        weeklyData[weekStart].count += 1;
      });

      // Calculate weekly averages and create scatter points
      const points = Object.keys(weeklyData).map((week) => {
        const weekData = weeklyData[week];
        const avgTime = weekData.totalTime / weekData.count;
        const volume = weekData.count;

        return {
          x: volume,
          y: avgTime,
          week,
          entryPoint,
        };
      });

      // Calculate overall average
      epData.avgTime = epData.totalTime / epData.count;

      // Add to total references
      insights.totalReferences += epData.count;

      // Determine if this is an optimal or bottleneck entry point
      const isHighVolume = epData.count > 20;
      const isEfficient = epData.avgTime <= KELLANOVA_THRESHOLDS.good; // Less than 72 hours

      if (isHighVolume && isEfficient) {
        insights.optimalEntryPoints.push({
          entryPoint,
          avgTime: epData.avgTime,
          volume: epData.count,
        });
      }

      if (isHighVolume && !isEfficient) {
        insights.bottleneckEntryPoints.push({
          entryPoint,
          avgTime: epData.avgTime,
          volume: epData.count,
        });
      }

      // Add dataset
      datasets.push({
        label: `Entry Point ${entryPoint}`,
        data: points,
        backgroundColor:
          entryPointColors[entryPoint] || entryPointColors.unknown,
        borderColor:
          entryPointColors[entryPoint]?.replace("0.7", "1") ||
          entryPointColors.unknown.replace("0.7", "1"),
        pointRadius: 8 + Math.sqrt(epData.count) / 2, // Size based on volume
        pointHoverRadius: 12 + Math.sqrt(epData.count) / 2,
        pointStyle: "circle",
      });
    });

    // Sort insights
    insights.optimalEntryPoints.sort((a, b) => a.avgTime - b.avgTime);
    insights.bottleneckEntryPoints.sort((a, b) => b.avgTime - a.avgTime);

    // Find optimal volume threshold
    const allPoints = datasets.flatMap((ds) => ds.data);
    const efficiencyByVolume = {};

    allPoints.forEach((point) => {
      const volumeBucket = Math.floor(point.x / 5) * 5; // Group by 5
      if (!efficiencyByVolume[volumeBucket]) {
        efficiencyByVolume[volumeBucket] = {
          times: [],
          totalTime: 0,
          count: 0,
        };
      }

      efficiencyByVolume[volumeBucket].times.push(point.y);
      efficiencyByVolume[volumeBucket].totalTime += point.y;
      efficiencyByVolume[volumeBucket].count += 1;
    });

    // Find volume with best efficiency
    let bestVolumeBucket = 0;
    let bestEfficiency = Number.MAX_VALUE;

    Object.keys(efficiencyByVolume).forEach((bucket) => {
      const bucketData = efficiencyByVolume[bucket];
      const avgTime = bucketData.totalTime / bucketData.count;

      // Only consider buckets with enough data points
      if (bucketData.count >= 3 && avgTime < bestEfficiency) {
        bestEfficiency = avgTime;
        bestVolumeBucket = parseInt(bucket, 10);
      }
    });

    insights.optimalVolumeThreshold = bestVolumeBucket;

    // Generate recommended actions
    if (insights.bottleneckEntryPoints.length > 0) {
      insights.recommendedActions.push(
        `Investigate process bottlenecks at Entry Points ${insights.bottleneckEntryPoints
          .map((bp) => bp.entryPoint)
          .join(", ")}`
      );
    }

    if (insights.optimalEntryPoints.length > 0) {
      insights.recommendedActions.push(
        `Consider standardizing processes using Entry Point ${insights.optimalEntryPoints[0].entryPoint} as a model`
      );
    }

    if (insights.optimalVolumeThreshold > 0) {
      insights.recommendedActions.push(
        `Target processing volume of approximately ${insights.optimalVolumeThreshold} references per week for optimal efficiency`
      );
    }

    // Analyze recent trend
    const recentPoints = allPoints.filter((p) => {
      const weekDate = new Date(p.week);
      return isWithinInterval(weekDate, {
        start: subDays(endDate, 30),
        end: endDate,
      });
    });

    if (recentPoints.length > 0) {
      const avgRecentTime =
        recentPoints.reduce((sum, p) => sum + p.y, 0) / recentPoints.length;
      const totalAvgTime =
        allPoints.reduce((sum, p) => sum + p.y, 0) / allPoints.length;

      if (avgRecentTime < totalAvgTime * 0.9) {
        insights.recentTrend = "improving";
      } else if (avgRecentTime > totalAvgTime * 1.1) {
        insights.recentTrend = "worsening";
      } else {
        insights.recentTrend = "stable";
      }
    }

    // Update state
    setChartData({ datasets });
    setInsights(insights);
  };

  // Chart options with manual background zones instead of annotations plugin
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        title: {
          display: true,
          text: "Weekly Volume (number of references)",
          font: {
            size: 12,
            weight: "bold",
          },
        },
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
        // Add a background grid to simulate zones
        grid: {
          display: true,
          color: (context) => {
            const value = context.tick.value;
            // Optimal volume line
            if (value === insights.optimalVolumeThreshold) {
              return "rgba(54, 162, 235, 0.5)";
            }
            return "rgba(200, 200, 200, 0.2)";
          },
          lineWidth: (context) => {
            const value = context.tick.value;
            // Optimal volume line
            if (value === insights.optimalVolumeThreshold) {
              return 2;
            }
            return 1;
          },
        },
      },
      y: {
        title: {
          display: true,
          text: "Average Processing Time (hours)",
          font: {
            size: 12,
            weight: "bold",
          },
        },
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return value + "h";
          },
        },
        // Color code efficiency zones
        grid: {
          display: true,
          color: (context) => {
            const value = context.tick.value;
            if (value === KELLANOVA_THRESHOLDS.good) {
              return "rgba(75, 192, 192, 0.5)"; // Good threshold (72h)
            } else if (value === KELLANOVA_THRESHOLDS.moderate) {
              return "rgba(255, 159, 64, 0.5)"; // Moderate threshold (121h)
            }
            return "rgba(200, 200, 200, 0.2)";
          },
          lineWidth: (context) => {
            const value = context.tick.value;
            if (value === KELLANOVA_THRESHOLDS.good || value === KELLANOVA_THRESHOLDS.moderate) {
              return 2;
            }
            return 1;
          },
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          title: function (context) {
            const point = context[0].raw;
            return `Entry Point ${point.entryPoint} - Week of ${point.week}`;
          },
          label: function (context) {
            const point = context.raw;
            return [
              `Volume: ${point.x} references`,
              `Avg. Processing Time: ${point.y.toFixed(1)} hours`,
            ];
          },
          footer: function (context) {
            const point = context[0].raw;
            let efficiency = "";

            if (point.y <= KELLANOVA_THRESHOLDS.good) {
              efficiency = "Good efficiency";
            } else if (point.y <= KELLANOVA_THRESHOLDS.moderate) {
              efficiency = "Moderate efficiency";
            } else {
              efficiency = "Low efficiency";
            }

            // Add note for optimal volume
            if (
              insights.optimalVolumeThreshold > 0 &&
              Math.abs(point.x - insights.optimalVolumeThreshold) <= 5
            ) {
              return `${efficiency} (Near optimal volume)`;
            }

            return efficiency;
          },
        },
      },
      legend: {
        position: "top",
      },
      datalabels: {
        display: false, // Disable data labels for clarity
      },
    },
    // Draw background zones on the chart
    onBeforeDraw: function (chart) {
      const ctx = chart.ctx;
      const chartArea = chart.chartArea;

      if (!chartArea) {
        return;
      }

      const xScale = chart.scales.x;
      const yScale = chart.scales.y;

      // Save the current state
      ctx.save();

      // Draw optimal zone (0-72 hours)
      const optimalY = yScale.getPixelForValue(KELLANOVA_THRESHOLDS.good);
      ctx.fillStyle = "rgba(75, 192, 192, 0.1)";
      ctx.fillRect(
        chartArea.left,
        chartArea.top,
        chartArea.width,
        optimalY - chartArea.top
      );

      // Draw moderate zone (72-121 hours)
      const moderateY = yScale.getPixelForValue(KELLANOVA_THRESHOLDS.moderate);
      ctx.fillStyle = "rgba(255, 159, 64, 0.1)";
      ctx.fillRect(
        chartArea.left,
        optimalY,
        chartArea.width,
        moderateY - optimalY
      );

      // Draw poor zone (121+ hours)
      ctx.fillStyle = "rgba(255, 99, 132, 0.1)";
      ctx.fillRect(
        chartArea.left,
        moderateY,
        chartArea.width,
        chartArea.bottom - moderateY
      );

      // Draw optimal volume line
      if (insights.optimalVolumeThreshold > 0) {
        const optimalX = xScale.getPixelForValue(
          insights.optimalVolumeThreshold
        );
        ctx.strokeStyle = "rgba(54, 162, 235, 0.5)";
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 6]);
        ctx.beginPath();
        ctx.moveTo(optimalX, chartArea.top);
        ctx.lineTo(optimalX, chartArea.bottom);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // Add zone labels
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.font = "12px Arial";

      // Optimal zone label
      ctx.fillStyle = "rgba(75, 192, 192, 0.8)";
      ctx.fillText("Optimal Zone", chartArea.left + 10, chartArea.top + 10);

      // Bottleneck zone label
      ctx.fillStyle = "rgba(255, 99, 132, 0.8)";
      ctx.fillText("Bottleneck Zone", chartArea.left + 10, moderateY + 10);

      // Restore the state
      ctx.restore();
    },
  };

  // Format time period text
  const formatTimePeriod = (period) => {
    switch (period) {
      case "week":
        return "Past Week";
      case "month":
        return "Past Month";
      case "quarter":
        return "Past Quarter";
      default:
        return "All Time";
    }
  };

  // Get trend icon and class
  const getTrendIndicator = (trend) => {
    if (trend === "improving") {
      return { icon: "↓", color: "text-green-600", text: "Improving" };
    } else if (trend === "worsening") {
      return { icon: "↑", color: "text-red-600", text: "Worsening" };
    } else {
      return { icon: "→", color: "text-gray-600", text: "Stable" };
    }
  };

  // Render loading spinner when data is loading
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <FontAwesomeIcon
          icon={faSpinner}
          className="animate-spin text-xl text-teal-600 mr-2"
        />
        <span className="text-gray-600">Loading efficiency matrix...</span>
      </div>
    );
  }

  // Render "no data" message when there is no data
  if (!data || data.length === 0 || chartData.datasets.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center h-full">
        <FontAwesomeIcon
          icon={faLightbulb}
          className="text-2xl text-gray-400 mb-2"
        />
        <span className="text-gray-500">No efficiency data available</span>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-4 w-full h-full flex flex-col">
      {/* Chart Header */}
      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-lg font-bold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faLightbulb} className="text-teal-600 mr-2" />
          Processing Efficiency Matrix
        </h2>

        {/* Time Period Controls */}
        <div className="flex space-x-2">
          <button
            className={`px-2 py-1 text-xs rounded-md transition-colors ${
              timePeriod === "week"
                ? "bg-teal-100 text-teal-700 font-medium"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            onClick={() => setTimePeriod("week")}
          >
            Week
          </button>
          <button
            className={`px-2 py-1 text-xs rounded-md transition-colors ${
              timePeriod === "month"
                ? "bg-teal-100 text-teal-700 font-medium"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            onClick={() => setTimePeriod("month")}
          >
            Month
          </button>
          <button
            className={`px-2 py-1 text-xs rounded-md transition-colors ${
              timePeriod === "quarter"
                ? "bg-teal-100 text-teal-700 font-medium"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            onClick={() => setTimePeriod("quarter")}
          >
            Quarter
          </button>
          <button
            className={`px-2 py-1 text-xs rounded-md transition-colors ${
              timePeriod === "all"
                ? "bg-teal-100 text-teal-700 font-medium"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            onClick={() => setTimePeriod("all")}
          >
            All
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <div className="bg-gray-50 rounded-md p-2">
          <div className="text-xs text-gray-500 mb-1">Total References</div>
          <div className="text-md font-bold">{insights.totalReferences}</div>
          <div className="text-xs text-gray-500 mt-1">
            {formatTimePeriod(timePeriod)}
          </div>
        </div>

        <div className="bg-teal-50 rounded-md p-2">
          <div className="text-xs text-gray-500 mb-1">Processing Trend</div>
          <div className="flex items-center">
            <div
              className={`text-md font-bold ${
                getTrendIndicator(insights.recentTrend).color
              }`}
            >
              {getTrendIndicator(insights.recentTrend).text}
            </div>
            <div
              className={`ml-1 ${
                getTrendIndicator(insights.recentTrend).color
              }`}
            >
              {getTrendIndicator(insights.recentTrend).icon}
            </div>
          </div>
        </div>

        <div className="bg-blue-50 rounded-md p-2">
          <div className="text-xs text-gray-500 mb-1">Optimal Volume/Week</div>
          <div className="text-md font-bold text-blue-700">
            {insights.optimalVolumeThreshold > 0
              ? `~${insights.optimalVolumeThreshold}`
              : "N/A"}
          </div>
        </div>

        <div className="bg-green-50 rounded-md p-2">
          <div className="text-xs text-gray-500 mb-1">Best Entry Point</div>
          <div className="text-md font-bold text-green-700">
            {insights.optimalEntryPoints.length > 0
              ? insights.optimalEntryPoints[0].entryPoint
              : "N/A"}
          </div>
        </div>
      </div>

      {/* Legend Information */}
      <div className="flex items-center space-x-4 mb-2 text-xs text-gray-600">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-teal-100 border border-teal-300 rounded-sm mr-1"></div>
          <span>Optimal</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-yellow-100 border border-yellow-300 rounded-sm mr-1"></div>
          <span>Moderate</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-red-100 border border-red-300 rounded-sm mr-1"></div>
          <span>Bottleneck</span>
        </div>
        <div className="flex items-center">
          <div className="w-6 h-0 border-b border-dashed border-blue-400 mr-1"></div>
          <span>Optimal Volume</span>
        </div>
      </div>

      {/* Chart Container */}
      <div className="flex-grow relative">
        <Scatter data={chartData} options={options} />
      </div>

      {/* Insights Footer */}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <div className="flex justify-between items-start">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Key Insights
            </h4>
            <ul className="text-xs text-gray-600 space-y-1">
              {insights.recommendedActions.map((action, index) => (
                <li key={index} className="flex items-start">
                  <span className="inline-block w-4 text-teal-500 mr-1">•</span>
                  <span>{action}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex space-x-1">
            <button
              title="Download chart"
              className="p-1 hover:text-teal-600 transition-colors"
            >
              <FontAwesomeIcon icon={faDownload} />
            </button>
            <button
              title="Expand chart"
              className="p-1 hover:text-teal-600 transition-colors"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <FontAwesomeIcon icon={faExpand} />
            </button>
            <button
              title="Filter data"
              className="p-1 hover:text-teal-600 transition-colors"
            >
              <FontAwesomeIcon icon={faFilter} />
            </button>
          </div>
        </div>
      </div>

      {/* Chart Info */}
      <div className="mt-2 text-xs text-gray-500 flex items-center">
        <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
        <span>
          Bubble size represents relative volume. Hover over points for details.
        </span>
      </div>

      {/* Expanded Modal */}
      {isExpanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setIsExpanded(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-5/6 h-5/6 max-w-7xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                Processing Efficiency Matrix Analysis
              </h3>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setIsExpanded(false)}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div
              className="p-4 overflow-y-auto"
              style={{ height: "calc(100% - 64px)" }}
            >
              {/* Matrix Explanation */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    About This Matrix
                  </h4>
                  <p className="text-sm text-gray-600 mb-2">
                    This matrix visualizes the relationship between weekly
                    processing volume and average processing time across
                    different entry points.
                  </p>
                  <p className="text-sm text-gray-600">
                    Each bubble represents a specific entry point during a
                    specific week. The size indicates relative volume, while
                    position shows efficiency.
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Optimal vs. Bottleneck Zones
                  </h4>
                  <div className="flex items-center mb-2">
                    <div className="w-3 h-3 rounded-md bg-teal-100 border border-teal-300 mr-2"></div>
                    <div className="text-sm">
                      <strong>Optimal Zone:</strong> High efficiency processing
                    </div>
                  </div>
                  <div className="flex items-center mb-2">
                    <div className="w-3 h-3 rounded-md bg-red-100 border border-red-300 mr-2"></div>
                    <div className="text-sm">
                      <strong>Bottleneck Zone:</strong> Process inefficiencies
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="w-8 h-0 border-b-2 border-dashed border-blue-400 mr-2"></div>
                    <div className="text-sm">
                      <strong>Optimal Volume:</strong> Most efficient processing
                      rate
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Entry Point Performance
                  </h4>
                  {insights.optimalEntryPoints.length > 0 && (
                    <div className="mb-3">
                      <div className="text-xs text-gray-500">
                        Best Performing
                      </div>
                      {insights.optimalEntryPoints
                        .slice(0, 2)
                        .map((ep, index) => (
                          <div key={index} className="flex items-center mt-1">
                            <FontAwesomeIcon
                              icon={getTransportIcon(ep.entryPoint).icon}
                              className={`${
                                getTransportIcon(ep.entryPoint).color
                              } mr-2`}
                            />
                            <div className="text-sm">
                              EP {ep.entryPoint}: {ep.avgTime.toFixed(1)}h avg.
                              ({ep.volume} refs)
                            </div>
                          </div>
                        ))}
                    </div>
                  )}

                  {insights.bottleneckEntryPoints.length > 0 && (
                    <div>
                      <div className="text-xs text-gray-500">
                        Needs Improvement
                      </div>
                      {insights.bottleneckEntryPoints
                        .slice(0, 2)
                        .map((ep, index) => (
                          <div key={index} className="flex items-center mt-1">
                            <FontAwesomeIcon
                              icon={getTransportIcon(ep.entryPoint).icon}
                              className={`${
                                getTransportIcon(ep.entryPoint).color
                              } mr-2`}
                            />
                            <div className="text-sm">
                              EP {ep.entryPoint}: {ep.avgTime.toFixed(1)}h avg.
                              ({ep.volume} refs)
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Large Chart */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm mb-6">
                <div style={{ height: "400px" }}>
                  <Scatter data={chartData} options={options} />
                </div>
              </div>

              {/* Recommendations and Insights */}
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 shadow-sm">
                <div className="flex items-start">
                  <FontAwesomeIcon
                    icon={faLightbulb}
                    className="text-teal-600 mt-1 mr-3"
                  />
                  <div>
                    <h4 className="text-md font-medium text-gray-800 mb-2">
                      Recommendations Based on Data
                    </h4>
                    <ul className="space-y-2">
                      {insights.recommendedActions.map((action, index) => (
                        <li key={index} className="flex items-start">
                          <span className="inline-block w-4 text-teal-500 mr-1">
                            •
                          </span>
                          <span className="text-sm">{action}</span>
                        </li>
                      ))}
                      <li className="flex items-start">
                        <span className="inline-block w-4 text-teal-500 mr-1">
                          •
                        </span>
                        <span className="text-sm">
                          Consider analyzing specific process differences
                          between high and low performing entry points to
                          identify best practices.
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="inline-block w-4 text-teal-500 mr-1">
                          •
                        </span>
                        <span className="text-sm">
                          Set performance targets based on the optimal
                          efficiency zone identified in this analysis and track
                          improvements over time.
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessingEfficiencyMatrixChart;
